// Custom hook for notification WebSocket integration

import { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  addNotification,
  updateNotification,
  updateUnreadCount,
  fetchNotifications,
  fetchNotificationSummary,
  NotificationItem,
  normalizeNotificationItem
} from '../store/notificationSlice';
import notificationWebSocketService from '../services/notificationWebSocket';
import websocketService from '../services/websocket';
import { showNotificationAlert } from '../store/notificationSlice';

export interface UseNotificationWebSocketOptions {
  autoConnect?: boolean;
  showBrowserNotifications?: boolean;
  showToastAlerts?: boolean;
  onNotificationReceived?: (notification: NotificationItem) => void;
  onConnectionChange?: (connected: boolean) => void;
}

let autoConnectOwnerCount = 0;

export const useNotificationWebSocket = (options: UseNotificationWebSocketOptions = {}) => {
  const {
    autoConnect = true,
    showBrowserNotifications = true,
    showToastAlerts = true,
    onNotificationReceived,
    onConnectionChange
  } = options;

  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const validationState = useSelector((state: RootState) => state.auth.validationState);
  const preferences = useSelector((state: RootState) => state.notifications.preferences);
  const notifications = useSelector((state: RootState) => state.notifications.notifications);
  
  const dataUnsubscribersRef = useRef<(() => void)[]>([]);
  const observerUnsubscribersRef = useRef<(() => void)[]>([]);
  const ownsAutoConnectionRef = useRef(false);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const isConnectedRef = useRef(false);
  const recentAlertKeysRef = useRef<Map<string, number>>(new Map());
  const notificationsRef = useRef<NotificationItem[]>(notifications);
  const authReadyRef = useRef(false);
  const bootstrapRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    authReadyRef.current = isAuthenticated && validationState === 'valid';
  }, [isAuthenticated, validationState]);

  const getUrgencyLevel = useCallback((notification: NotificationItem): 'routine' | 'urgent' | 'critical' => {
    const metadataUrgency = notification.metadata?.urgencyLevel;
    if (metadataUrgency === 'urgent' || metadataUrgency === 'critical') {
      return metadataUrgency;
    }

    if (notification.priority === 'critical') {
      return 'critical';
    }

    if (notification.priority === 'high') {
      return 'urgent';
    }

    return 'routine';
  }, []);

  const shouldDispatchAlert = useCallback((notification: NotificationItem) => {
    const messageId = notification.metadata?.messageId;
    const alertKey = messageId ? `message:${messageId}` : `notification:${notification.id}`;
    const now = Date.now();
    const duplicateWindowMs = 3000;
    const cache = recentAlertKeysRef.current;

    for (const [key, timestamp] of Array.from(cache.entries())) {
      if (now - timestamp > duplicateWindowMs) {
        cache.delete(key);
      }
    }

    const previousTimestamp = cache.get(alertKey);
    if (previousTimestamp && now - previousTimestamp < duplicateWindowMs) {
      return false;
    }

    cache.set(alertKey, now);
    return true;
  }, []);

  const clearBootstrapRetry = useCallback(() => {
    if (bootstrapRetryTimeoutRef.current) {
      clearTimeout(bootstrapRetryTimeoutRef.current);
      bootstrapRetryTimeoutRef.current = null;
    }
  }, []);

  const extractMessageId = useCallback((notification: NotificationItem) => {
    return notification.metadata?.messageId || undefined;
  }, []);

  const upsertPersistedNotification = useCallback((rawNotification: NotificationItem, options?: { showAlerts?: boolean }) => {
    const notification = normalizeNotificationItem(rawNotification);
    const notificationMessageId = extractMessageId(notification);
    const existingNotification = notificationsRef.current.find(existing =>
      existing.id === notification.id ||
      (
        !!notificationMessageId &&
        !!existing.metadata?.messageId &&
        existing.metadata.messageId === notificationMessageId
      )
    );

    if (existingNotification) {
      dispatch(updateNotification(notification));
    } else {
      dispatch(addNotification(notification));
    }

    if (options?.showAlerts === false) {
      return notification;
    }

    return notification;
  }, [dispatch, extractMessageId]);

  const refreshPersistedNotifications = useCallback((allowRetry: boolean = false) => {
    clearBootstrapRetry();

    const runRefresh = async (attempt: number) => {
      try {
        await Promise.all([
          dispatch(fetchNotifications(undefined) as any).unwrap(),
          dispatch(fetchNotificationSummary() as any).unwrap()
        ]);
      } catch (error) {
        if (!allowRetry || attempt >= 2 || !authReadyRef.current) {
          return;
        }

        bootstrapRetryTimeoutRef.current = setTimeout(() => {
          void runRefresh(attempt + 1);
        }, 1000 * (attempt + 1));
      }
    };

    void runRefresh(0);
  }, [clearBootstrapRetry, dispatch]);

  // Show browser notification
  const showBrowserNotification = useCallback((notification: NotificationItem, options?: { force?: boolean }) => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      const force = options?.force === true;
      const browserNotification = new Notification(notification.content?.title || 'New Notification', {
        body: notification.content?.preview || notification.content?.body || '',
        // Both files never existed, so notifications showed no icon at all.
        // logo192 is the app icon tile, and is what websocket.ts already uses.
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: notification.id,
        requireInteraction: notification.priority === 'critical',
        silent: force ? false : !preferences?.enable_sound_alerts,
        data: {
          notificationId: notification.id,
          conversationId: notification.metadata.conversationId,
          type: notification.type
        }
      });

      browserNotification.onclick = () => {
        window.focus();
        
        // Navigate to relevant page based on notification type
        if (notification.metadata.conversationId) {
          // Navigate to chat
          window.location.hash = `#/chat/${notification.metadata.conversationId}`;
        }
        
        browserNotification.close();
      };

      // Auto close after 5 seconds for non-critical notifications
      if (notification.priority !== 'critical') {
        setTimeout(() => {
          browserNotification.close();
        }, 5000);
      }
    }
  }, [preferences]);

  // Handle new notifications
  const handleNotification = useCallback((incomingNotification: NotificationItem) => {
    const notification = upsertPersistedNotification(incomingNotification);
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('🔔 DEBUG: Received new notification:', notification);
      console.log('🎯 DEBUG: Notification metadata:', notification.metadata);
      console.log('📊 DEBUG: Priority info:', {
        'metadata.urgencyLevel': notification.metadata.urgencyLevel,
        'metadata.urgencyReason': notification.metadata.urgencyReason,
        'will use urgency level': notification.metadata.urgencyLevel || 'routine'
      });
    }
    
    const urgencyLevel = getUrgencyLevel(notification);
    const isUrgentOrCritical = urgencyLevel === 'urgent' || urgencyLevel === 'critical';
    
    // Show browser notification if enabled
    if (showBrowserNotifications && (isUrgentOrCritical || preferences?.enable_desktop_notifications)) {
      showBrowserNotification(notification, { force: isUrgentOrCritical });
    }
    
    // Show toast alert if enabled
    if (showToastAlerts && shouldDispatchAlert(notification)) {
      const alertData = {
        id: `notification-${notification.id}`,
        type: (notification.type === 'group_mention' ? 'group_message' : 'message') as 'message' | 'group_message',
        title: notification.content?.title || 'New Notification',
        message: notification.content?.preview || notification.content?.body || '',
        senderName: notification.sender?.name || 'System',
        conversationId: notification.metadata.conversationId,
        groupId: notification.metadata.groupId ? parseInt(notification.metadata.groupId) : undefined,
        messageId: notification.metadata.messageId,
        timestamp: Date.now(),
        urgencyLevel,
        urgencyReason: notification.metadata.urgencyReason,
        forceSound: isUrgentOrCritical
      };
      
      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('🚨 DEBUG: Creating notification alert with data:', alertData);
        console.log('⚡ DEBUG: Final urgencyLevel being passed:', alertData.urgencyLevel);
      }
      
      dispatch(showNotificationAlert(alertData));
    }
    
    // Call custom callback
    if (onNotificationReceived) {
      onNotificationReceived(notification);
    }
  }, [
    dispatch,
    getUrgencyLevel,
    onNotificationReceived,
    preferences,
    shouldDispatchAlert,
    showBrowserNotification,
    showBrowserNotifications,
    showToastAlerts,
    upsertPersistedNotification
  ]);

  const handleCompatibilityNotification = useCallback((incomingNotification: NotificationItem) => {
    upsertPersistedNotification(incomingNotification, { showAlerts: false });
  }, [upsertPersistedNotification]);

  // Handle unread count updates
  const handleUnreadCount = useCallback((count: number) => {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('📊 Updated unread count:', count);
    }
    dispatch(updateUnreadCount(count));
  }, [dispatch]);

  // Handle connection status changes
  const handleConnection = useCallback((connected: boolean) => {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log(`🔌 Notification WebSocket ${connected ? 'connected' : 'disconnected'}`);
    }
    isConnectedRef.current = connected;
    
    if (connected) {
      // Clear error on successful connection
      setConnectionError(null);
      // Only the auto-connect owner should request unread count on connect
      if (ownsAutoConnectionRef.current) {
        notificationWebSocketService.requestUnreadCount();
      }
    }
    
    if (onConnectionChange) {
      onConnectionChange(connected);
    }
  }, [onConnectionChange]);

  // Handle connection state changes
  const handleStateChange = useCallback((state: string) => {
    setConnectionState(state);
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[NotificationWS] Connection state:', state);
    }
    
    // Set error message for error state
    if (state === 'error') {
      setConnectionError('Failed to connect to notification service');
    } else if (state === 'connected') {
      setConnectionError(null);
    }
  }, []);

  // Handle notification updates
  const handleUpdate = useCallback((data: any) => {
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[NotificationWS] Received update:', data);
    }
    
    if (data.action === 'bulk_update' && data.payload?.notifications) {
      // Handle bulk updates
      data.payload.notifications.forEach((notification: NotificationItem) => {
        dispatch(updateNotification(notification));
      });
    }
  }, [dispatch]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return Notification.permission === 'granted';
  }, []);

  // Subscribe to connection state in all modes (including autoConnect: false)
  useEffect(() => {
    setConnectionState(notificationWebSocketService.getConnectionState());
    isConnectedRef.current = notificationWebSocketService.isConnected();

    const unsubscribeConnection = notificationWebSocketService.onConnection(handleConnection);
    const unsubscribeStateChange = notificationWebSocketService.onStateChange(handleStateChange);

    observerUnsubscribersRef.current = [unsubscribeConnection, unsubscribeStateChange];

    return () => {
      observerUnsubscribersRef.current.forEach(unsubscribe => unsubscribe());
      observerUnsubscribersRef.current = [];
    };
  }, [handleConnection, handleStateChange]);

  // Data handlers are only attached by auto-connect hook instances
  useEffect(() => {
    const shouldAttachDataHandlers = autoConnect && isAuthenticated && validationState === 'valid';
    if (!shouldAttachDataHandlers) {
      dataUnsubscribersRef.current.forEach(unsubscribe => unsubscribe());
      dataUnsubscribersRef.current = [];
      return;
    }

    const unsubscribeNotification = notificationWebSocketService.onNotification(handleNotification);
    const unsubscribeUnreadCount = notificationWebSocketService.onUnreadCount(handleUnreadCount);
    const unsubscribeUpdate = notificationWebSocketService.onUpdate(handleUpdate);
    const unsubscribeCompatibilityNotification = websocketService.onNotification(handleCompatibilityNotification);

    dataUnsubscribersRef.current = [
      unsubscribeNotification,
      unsubscribeUnreadCount,
      unsubscribeUpdate,
      unsubscribeCompatibilityNotification
    ];

    return () => {
      dataUnsubscribersRef.current.forEach(unsubscribe => unsubscribe());
      dataUnsubscribersRef.current = [];
    };
  }, [
    autoConnect,
    isAuthenticated,
    validationState,
    handleCompatibilityNotification,
    handleNotification,
    handleUnreadCount,
    handleUpdate
  ]);

  // Optional listener mode for pages that need real-time callbacks without owning the WS lifecycle
  useEffect(() => {
    if (autoConnect || !onNotificationReceived || !isAuthenticated || validationState !== 'valid') {
      return;
    }

    const unsubscribeNotification = notificationWebSocketService.onNotification(onNotificationReceived);
    return () => {
      unsubscribeNotification();
    };
  }, [autoConnect, onNotificationReceived, isAuthenticated, validationState]);

  // Connect/disconnect lifecycle
  useEffect(() => {
    if (!autoConnect) {
      return;
    }

    const releaseOwnership = () => {
      if (ownsAutoConnectionRef.current) {
        ownsAutoConnectionRef.current = false;
        autoConnectOwnerCount = Math.max(0, autoConnectOwnerCount - 1);
      }
      if (autoConnectOwnerCount === 0) {
        notificationWebSocketService.disconnect();
      }
    };

    const isReady = isAuthenticated && validationState === 'valid';

    if (isReady) {
      if (!ownsAutoConnectionRef.current) {
        ownsAutoConnectionRef.current = true;
        autoConnectOwnerCount += 1;
      }

      if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('🔔 useNotificationWebSocket initializing for authenticated user');
      }

      // Expose global notification alert function for WebSocket service
      (window as any).showGlobalNotificationAlert = (alertData: any) => {
        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('🌍 DEBUG: Global notification alert triggered by hook:', alertData);
          console.log('🔍 DEBUG: Alert source and urgency:', {
            'alertData.id': alertData.id,
            'alertData.urgencyLevel': alertData.urgencyLevel,
            'alertData.senderName': alertData.senderName,
            'called from': 'useNotificationWebSocket hook'
          });
        }

        dispatch(showNotificationAlert(alertData));
      };

      const currentState = notificationWebSocketService.getConnectionState();
      if (!notificationWebSocketService.isConnected() && currentState !== 'connecting') {
        if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
          console.log('🔔 Connecting notification WebSocket service');
        }
        notificationWebSocketService.connect();
      } else if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
        console.log('🔔 Notification WebSocket already connected/connecting, skipping connection');
      }

      refreshPersistedNotifications(true);
    } else {
      // Not ready (logged out / validating)
      delete (window as any).showGlobalNotificationAlert;
      clearBootstrapRetry();
      releaseOwnership();
    }

    return () => {
      delete (window as any).showGlobalNotificationAlert;
      clearBootstrapRetry();
      releaseOwnership();
    };
  }, [autoConnect, clearBootstrapRetry, isAuthenticated, refreshPersistedNotifications, validationState, dispatch]);

  useEffect(() => {
    if (!autoConnect || !isAuthenticated || validationState !== 'valid') {
      return;
    }

    const recoverNotifications = () => {
      const currentState = notificationWebSocketService.getConnectionState();
      if (!notificationWebSocketService.isConnected() && currentState !== 'connecting') {
        notificationWebSocketService.connect();
      }
      refreshPersistedNotifications(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recoverNotifications();
      }
    };

    const handleOnline = () => {
      recoverNotifications();
    };

    // Mobile bfcache restores fire `pageshow` with persisted=true, not
    // visibilitychange — without this, a bfcache-restored page never
    // reconnects the notification WebSocket.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        recoverNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [autoConnect, isAuthenticated, refreshPersistedNotifications, validationState]);

  // Manual control methods
  const connect = useCallback(() => {
    if (!isAuthenticated) {
      console.warn('Cannot connect notification WebSocket: not authenticated');
      return;
    }
    notificationWebSocketService.connect();
  }, [isAuthenticated]);

  const disconnect = useCallback(() => {
    notificationWebSocketService.disconnect();
  }, []);

  const markAsRead = useCallback((notificationIds: string[]) => {
    if (notificationWebSocketService.isConnected()) {
      notificationWebSocketService.markNotificationsAsRead(notificationIds);
    }
  }, []);

  const refreshUnreadCount = useCallback(() => {
    if (notificationWebSocketService.isConnected()) {
      notificationWebSocketService.requestUnreadCount();
    }
  }, []);

  const reconnect = useCallback(() => {
    setConnectionError(null);
    if (isAuthenticated) {
      notificationWebSocketService.connect();
    }
  }, [isAuthenticated]);

  // Return state and control methods
  return {
    isConnected: isConnectedRef.current,
    connectionState,
    connectionError,
    connect,
    disconnect,
    reconnect,
    markAsRead,
    refreshUnreadCount,
    requestNotificationPermission,
    hasNotificationPermission: typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted',
    isConnecting: connectionState === 'connecting',
    hasError: connectionState === 'error'
  };
};
